import { Observable, ReplaySubject } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { Auth0Service } from '../../services/auth0.service';
import { AuthService } from '../../services/auth.service';
import { Injectable } from '@angular/core';
import { OnboardAuthService } from '../onboard-auth-service/onboard-auth.service';
import { Profile } from '../../models/profile';
import { ProfileApiService } from '../profile-api-service/profile-api.service';
import { config } from '../../../assets/config-constants';

@Injectable()
export class ProfileService {
  private _authProfile: any;
  private _existingProfile: boolean;

  private _localProfileCopy: Profile;
  private _profile: ReplaySubject<Profile> = new ReplaySubject<Profile>(1);
  public profile$: Observable<Profile> = this._profile.asObservable();

  public gettingProfile = false;
  public emailVerified = false;
  public internal = false;
  public companyName;

  constructor(
    public _apiSvc: ProfileApiService,
    public _onboardAuth: OnboardAuthService,
    public _auth: AuthService,
    public _auth0Service: Auth0Service,
  ) {
    this.getProfile();
  }

  public saveOnBoardProfile(): Observable<any> {
    return this.profile$.pipe(
      mergeMap((profile) => {
        // snag the first name and last name from the Auth Profile
        profile.firstName = this._authProfile[
          config.claimUserMetadata
        ].first_name;
        profile.lastName = this._authProfile[
          config.claimUserMetadata
        ].last_name;

        // create or save depending on if this is a new profile or not
        if (this._existingProfile) {
          return this.saveFinal(profile);
        } else {
          return this.createFinal(profile);
        }
      })
    );
  }

  public tosAccept(profile: Profile): Observable<boolean> {
    return this._apiSvc.tosAccept(this._authProfile.sub, profile);
  }

  public consentAccept(): Observable<boolean> {
    return this._apiSvc.consentAccept();
  }

  public consentRevoke(): Observable<boolean> {
    return this._apiSvc.consentRevoke();
  }

  public saveProfile(): Observable<any> {
    return this.profile$.pipe(
      mergeMap((profile) => {
        const phone = profile.phone;
        const regex = /[0-9]/g;
        const digits = phone && phone.match(regex);
        profile.phone = digits && digits.join('');
        return this.saveFinal(profile);
      })
    );
  }

  public deleteProfile(): Observable<boolean> {
    return this._apiSvc.deleteProfile();
  }

   public deleteUser(): Observable<boolean> {
    return this._apiSvc.deleteUser(this._authProfile.sub);
  }

  public cancelDeleteProfile(): Observable<boolean> {
    return this._apiSvc.cancelDeleteProfile();
  }

  public changePassword(): Observable<any> {
    return this._auth0Service.changePassword(this._localProfileCopy.email);
  }

  public getChangePassword():Observable<string>{
    return this._apiSvc.getChangePasswordTicket();
  }

  public getProfile() {
    this.gettingProfile = true;

    // merging the two different auths into one - using whatever comes in as our auth
    let profileSub = null;
    if (this._auth.onboarding) {
      profileSub = this._onboardAuth.profile;
    } else {
      profileSub = this._auth.user$;
    }    
    if (profileSub) {
      profileSub.subscribe((authprof) => {
        if (!authprof) {
          return;
        }

        if (!this._localProfileCopy) {
          this.internal = authprof[config.claimUserType] === 'internal';
          this._authProfile = authprof;
          if(!this.internal){
            this.getProfileWithToken();
          }
        }
      });
    }
  }

  private getProfileWithToken(): void {
    this._auth.getTokenSilently$().pipe(
      filter(token => !!token)
    ).subscribe({
      next: (token) => {
        this.getAPIProfile();
      },
      error: (error) => {
        console.error('Error getting token:', error);
      }
    });
  }

  private getAPIProfile(): void {
    this._apiSvc.getProfile(this._authProfile.sub).subscribe(
      // we have a profile in CRM already - so use that
      (prof) => {
        if(!prof.email){
          prof.email= this._authProfile.email;
        }
        this._existingProfile = true;
        this._localProfileCopy = prof;
        this._profile.next(prof);
        this.gettingProfile = false;
      },
      // this is a new profile - so start with an empty profile
      (error) => this.createNewProfile()
    );
  }

  private createNewProfile(): void {
    this._existingProfile = false;
    const prof: Profile = {
      canWeContact: false,
      canWeShareInfo: true,
      companyEditable: this.companyName ? false : true,
      companyName: this.companyName || '',
      country: '',
      email: this._authProfile.email,
      firstName: '',
      lastName: '',
      personaID: 0,
      image: '',
      phoneCode: '',
      phone: '',
      postalCode: '',
      stateProvince: '',
      tosAccepted: false,
    };
    this._localProfileCopy = prof;
    this._profile.next(prof);
    this.gettingProfile = false;
  }

  private createFinal(profile: Profile): Observable<any> {
    return this._apiSvc.createProfile(profile);
  }

  private saveFinal(profile: Profile): Observable<any> {
    return this._apiSvc
      .saveProfile(this._authProfile.sub, profile);
  }
}